import React, { useState, useEffect } from 'react';
import { encryptStorage } from '../../constant';
import { PostRegistration, GetSessions } from '../../service/SessionAPI';
import { ConfirmationPopup } from './ConfirmationPopup';
import { Toast } from './Toast';

export const SessionRegistration = ({
    session,
    onClose,
    isEditMode = false,
    status,
    fetchSessions,
    empId: externalEmpId,
    currentSlotId,
    participantId,
    empName
}) => {
    const slots = session.slots || [];
    const empId = externalEmpId || encryptStorage.getItem('employeeId');
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [availableSlots, setAvailableSlots] = useState([]);
    const [modalMode, setModalMode] = useState(status === 'Register' ? 'edit' : 'register'); // 'register', 'edit', 'reschedule', 'reschedule-empty', 'cancel-confirm'
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (slots.length > 0) {
            if (currentSlotId) {
                const found = slots.find(s => s.slot_id === currentSlotId);
                setSelectedSlot(found || slots[0]);
            } else {
                setSelectedSlot(slots[0]);
            }
        }
    }, [slots, currentSlotId]);

    useEffect(() => {
        // Fetch available slots when the component mounts or session changes
        const fetchAvailableSlots = async () => {
            try {
                const response = await GetSessions(empId);
                const fetchedSlots = response.data.data.sessions.find(s => s.session_id === session.session_id)?.slots || [];

                setAvailableSlots(fetchedSlots);
            } catch (error) {
                console.error("Error fetching sessions:", error);
            }
        };
        fetchAvailableSlots();

    }, [session, empId]);

    const parseDaysLeft = (daysLeft) => {
        if (daysLeft === "Past") return -1;
        if (typeof daysLeft === "string" && daysLeft.includes("days")) {
            return parseInt(daysLeft.split(" ")[0], 10);
        }
        if (typeof daysLeft === "string" && daysLeft.includes("month")) {
            return 30; // Approximate a month as 30 days
        }
        return 999; // Future date
    };

    const getOtherValidSlots = () => {
        return availableSlots
            .filter(slot => {
                const isActive = slot.is_active;
                const daysLeft = parseDaysLeft(slot.days_left);
                const isRegisteredSlot = slot.status == 'Register';
                const hasAvailableSeats = slot.registered_count < slot.seat_available;

                return (
                    isActive &&
                    daysLeft > 7 &&
                    hasAvailableSeats &&
                    !isRegisteredSlot
                );
            })
            .map(slot => {
                // Adjust this if seat_available is total capacity
                const totalSeats = slot.registered_count + slot.seat_available;

                const percentage = totalSeats > 0 ? (slot.registered_count / slot.seat_available) * 100 : 0;

                if (percentage === 100) {
                    slot.seatClass = 'seats-closed';
                } else if (percentage <= 50) {
                    slot.seatClass = 'seats-available';
                } else if (percentage <= 80) {
                    slot.seatClass = 'seats-limited';
                } else {
                    slot.seatClass = 'seats-few';
                }
                return slot;
            });
    };

    // -- API Handlers --

    const handleRegister = async () => {
        if (!selectedSlot) {
            Toast.error("Please select a slot before registering.");
            return;
        }
        setLoading(true);
        const payload = {
            session_id: session.session_id,
            slot_id: selectedSlot.slot_id,
            emp_id: empId,
            status: 'Register'
        };
        try {
            const res = await PostRegistration(payload);
            if (res.data.code === 200) {
                Toast.success("Session Registered Successfully");
                fetchSessions && fetchSessions(); // Refresh session data
                onClose && onClose(); // Close modal
            } else {
                Toast.error("Failed to Register the session");
            }
        } catch (error) {
            console.error("Error during registration:", error); // Log error
            Toast.error("Failed to Register the session");
        } finally {
            setLoading(false);
        }
    };


    const handleReschedule = () => {
        const otherValidSlots = getOtherValidSlots();

        if (otherValidSlots.length > 0) {
            setModalMode('reschedule');
        } else {
            setModalMode('reschedule-empty');
        }
    };

    const handleConfirmReschedule = async () => {
        const registeredSlot = session.slots.find(
            slot => (slot.status === "Register" || slot.status === "Reschedule") && slot.participant_id
        );

        if (!registeredSlot) {
            Toast.error("No registration found to cancel.");
            setLoading(false);
            return;
        }

        setLoading(true);
        const payload = {
            session_id: session.session_id,
            slot_id: selectedSlot.slot_id,
            emp_id: empId,
            participant_id: registeredSlot.participant_id,
            status: 'Reschedule'
        };
        try {
            const res = await PostRegistration(payload);
            if (res.data.code === 200) {
                Toast.success("Session Rescheduled Successfully");
                fetchSessions && fetchSessions();
                onClose && onClose();
            } else {
                Toast.error("Failed to Reschedule the session");
            }
        } catch (error) {
            Toast.error("Failed to Reschedule the session");
        } finally {
            setLoading(false);
        }
    };

    const handleCancelRegistration = () => setModalMode('cancel-confirm');

    const handleConfirmCancel = async () => {
        setLoading(true);
        const registeredSlot = session.slots.find(
            slot => (slot.status === "Register" || slot.status === "Reschedule") && slot.participant_id
        );

        if (!registeredSlot) {
            Toast.error("No registration found to cancel.");
            setLoading(false);
            return;
        }
        const payload = {
            session_id: session.session_id,
            slot_id: registeredSlot.slot_id,
            emp_id: empId,
            participant_id: registeredSlot.participant_id,
            status: 'Remove'
        };
        try {
            const res = await PostRegistration(payload);
            if (res.data.code === 200) {
                Toast.success("Session Cancelled Successfully");
                fetchSessions && fetchSessions();
                onClose && onClose();
            } else {
                Toast.error("Failed to cancel the session");
            }
        } catch (error) {
            Toast.error("Failed to cancel the session");
        } finally {
            setLoading(false);
        }
    };

    // -- Helper --
    const getSeatClassName = (registeredCount, seatAvailable, daysLeft) => {
        const totalSeats = registeredCount + seatAvailable;
        if (totalSeats === 0 || daysLeft < 0) return 'seats-closed';
        const percentage = (registeredCount / seatAvailable) * 100;
        if (percentage === 100) return 'seats-closed';
        if (percentage <= 50) return 'seats-available';
        if (percentage <= 80) return 'seats-limited';
        return 'seats-few';
    };

    const formatSessionDate = (dateStr) => {
        if (!dateStr) return '';
        const [year, month, day] = dateStr.split('-').map(Number);
        const date = new Date(Date.UTC(year, month - 1, day)); // treat as UTC date, avoid timezone offset
        const options = { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' };
        return date.toLocaleDateString('en-GB', options);
    };

    // -- Modal Renderers --
    const renderRegisterModal = () => {
        const activeSlot = slots.filter(slot => slot.is_active);
        // Check if there are no active slots
        if (activeSlot.length === 0) {
            return (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
                    <div className="modal-dialog sessions-modal">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h6 className="modal-title">Register Session - {session.session_name}</h6>
                                <button type="button" className="btn-close font-12" onClick={onClose}></button>
                            </div>
                            <div className="modal-body">
                                <div className="row">
                                    <div className="col-md-12 my-4">
                                        <div className="d-flex flex-column justify-content-center align-items-center">
                                            <img src="Images/table-empty-state.svg" alt="info-icon" />
                                            <p className="font-14 semibold-text mt-4 mb-1">No Slots Available!</p>
                                            <p className="font-12 regular-text grey-text mb-1">Please contact admin for further assistance.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }
        return (
            <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
                <div className="modal-dialog sessions-modal">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h6 className="modal-title">Register Session - {session.session_name}</h6>
                            <button type="button" className="btn-close font-12" onClick={onClose}></button>
                        </div>
                        <div className="modal-body">
                            <div className="row">
                                <div className="col-md-12 mb-4">
                                    <div className="session-dates-toggle d-flex flex-wrap gap-2">
                                        {activeSlot.map((slot, index) => {
                                            // Compute seat class for each slot based on its availabilit
                                            const daysLeft = parseDaysLeft(slot.days_left);
                                            const seatClass = getSeatClassName(slot.registered_count, slot.seat_available, daysLeft);
                                            const isDisabled = seatClass === 'seats-closed';
                                            return (
                                                <React.Fragment key={index}>
                                                    <input
                                                        type="radio"
                                                        className="btn-check"
                                                        name="radio-grp"
                                                        id={`radio${index}`}
                                                        disabled={isDisabled}
                                                        checked={selectedSlot?.slot_id === slot.slot_id}
                                                        onChange={() => !isDisabled && setSelectedSlot(slot)}
                                                    />
                                                    <label
                                                        className={`btn font-11 semibold-text ${seatClass}`} // Apply seatClass here
                                                        htmlFor={`radio${index}`}
                                                    >
                                                        {formatSessionDate(slot.session_date)}
                                                    </label>
                                                </React.Fragment>
                                            );
                                        })}
                                    </div>
                                </div>
                                <div className="col-md-6 pe-2">
                                    <div className="slot-details d-flex flex-column align-items-start justify-content-start px-3 py-2">
                                        <p className="font-12 regular-text grey-text mb-1">Timings</p>
                                        <p className="font-12 semibold-text mb-0">{selectedSlot ? `${selectedSlot.start_time} - ${selectedSlot.end_time}` : 'N/A'}</p>
                                    </div>
                                </div>
                                <div className="col-md-6 ps-2">
                                    <div className="slot-details d-flex flex-column align-items-start justify-content-start px-3 py-2">
                                        <p className="font-12 regular-text grey-text mb-1">Duration</p>
                                        <p className="font-12 semibold-text mb-0">{selectedSlot ? selectedSlot.duration : 'N/A'}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer border-0">
                            <button className="btn hive-btn-secondary me-3" onClick={onClose} disabled={loading}>Cancel</button>
                            <button
                                className="btn hive-btn-primary"
                                onClick={handleRegister}
                                disabled={!selectedSlot || getSeatClassName(selectedSlot.registered_count, selectedSlot.seat_available) === 'seats-closed' || loading}
                            >
                                {loading ? "Registering..." : "Register"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )
    };

    const renderEditModal = () => {
        const registeredSlot = session.slots.find(
            slot => (slot.status === "Register" || slot.status === "Reschedule") && slot.participant_id
        );
        return (
            <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
                <div className="modal-dialog sessions-modal">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h6 className="modal-title">Edit Registration - {session.session_name}</h6>
                            <button type="button" className="btn-close font-12" onClick={onClose}></button>
                        </div>
                        <div className="modal-body">
                            <div className="row">
                                <div className="col-md-12 mb-4">
                                    <p className="font-12 regular-text grey-text mb-1">Session Date</p>
                                    <label className="font-11 semibold-text session-date-label shadow-sm flex-wrap radio-grp">{registeredSlot ? formatSessionDate(registeredSlot.session_date) : 'N/A'}</label>
                                </div>
                                <div className="col-md-6 pe-2">
                                    <div className="slot-details d-flex flex-column align-items-start justify-content-start px-3 py-2">
                                        <p className="font-12 regular-text grey-text mb-1">Timings</p>
                                        <p className="font-12 semibold-text mb-0 ">{selectedSlot ? `${selectedSlot.start_time} - ${selectedSlot.end_time}` : 'N/A'}</p>
                                    </div>
                                </div>
                                <div className="col-md-6 ps-2">
                                    <div className="slot-details d-flex flex-column align-items-start justify-content-start px-3 py-2">
                                        <p className="font-12 regular-text grey-text mb-1">Duration</p>
                                        <p className="font-12 semibold-text mb-0">{selectedSlot ? selectedSlot.duration : 'N/A'}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer border-0">
                            <button className="btn hive-btn-secondary me-3" onClick={handleCancelRegistration} disabled={loading}>Cancel Registration</button>
                            <button
                                className="btn hive-btn-primary"
                                onClick={handleReschedule}
                                disabled={loading}
                            >
                                Reschedule
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )
    };


    const renderRescheduleModal = () => {
        const otherValidSlots = getOtherValidSlots(); // Get filtered slots with seatClass

        return (
            <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
                <div className="modal-dialog sessions-modal">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h6 className="modal-title">Reschedule Session - {session.session_name}</h6>
                            <button type="button" className="btn-close font-12" onClick={() => setModalMode('edit')}></button>
                        </div>
                        <div className="modal-body">
                            <div className="row">
                                <div className="col-md-12 mb-4">
                                    <div className="session-dates-toggle d-flex flex-wrap gap-2">
                                        {otherValidSlots.map((slot, index) => {
                                            const isDisabled = slot.seatClass === 'seats-closed'; // Define isDisabled based on seatClass
                                            return (
                                                <React.Fragment key={index}>
                                                    <input
                                                        type="radio"
                                                        className="btn-check"
                                                        name="reschedule-radio-grp"
                                                        id={`rescheduleRadio${index}`}
                                                        disabled={isDisabled} // Disable if seats are closed
                                                        checked={selectedSlot?.slot_id === slot.slot_id}
                                                        onChange={() => !isDisabled && setSelectedSlot(slot)}
                                                    />
                                                    <label
                                                        className={`btn font-11 semibold-text ${slot.seatClass}`} // Use seatClass here
                                                        htmlFor={`rescheduleRadio${index}`}
                                                    >
                                                        {formatSessionDate(slot.session_date)}
                                                    </label>
                                                </React.Fragment>
                                            );
                                        })}
                                    </div>
                                </div>
                                {selectedSlot && (
                                    <>
                                        <div className="col-md-6 pe-2">
                                            <div className="slot-details d-flex flex-column align-items-start justify-content-start px-3 py-2">
                                                <p className="font-12 regular-text grey-text mb-1">Timings</p>
                                                <p className="font-12 semibold-text mb-0">{`${selectedSlot.start_time} - ${selectedSlot.end_time}`}</p>
                                            </div>
                                        </div>
                                        <div className="col-md-6 ps-2">
                                            <div className="slot-details d-flex flex-column align-items-start justify-content-start px-3 py-2">
                                                <p className="font-12 regular-text grey-text mb-1">Duration</p>
                                                <p className="font-12 semibold-text mb-0">{selectedSlot.duration}</p>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                        <div className="modal-footer border-0">
                            <button className="btn hive-btn-secondary me-3" onClick={() => setModalMode('edit')} disabled={loading}>Cancel</button>
                            <button
                                className="btn hive-btn-primary"
                                onClick={handleConfirmReschedule}
                                disabled={!selectedSlot || selectedSlot.seatClass === 'seats-closed' || loading} // Disable if no slot selected or if selected slot is closed
                            >
                                {loading ? "Rescheduling..." : "Confirm Reschedule"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderRescheduleEmptyState = () => (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
            <div className="modal-dialog sessions-modal">
                <div className="modal-content">
                    <div className="modal-header">
                        <h6 className="modal-title">Reschedule Session - {session.session_name}</h6>
                        <button type="button" className="btn-close font-12" onClick={() => setModalMode('edit')}></button>
                    </div>
                    <div className="modal-body">
                        <div className="row">
                            <div className="col-md-12 my-4">
                                <div className="d-flex flex-column justify-content-center align-items-center">
                                    <img src="Images/popup-info-icon.svg" alt="info-icon" />
                                    <p className="font-14 semibold-text mt-4 mb-1">No other slots available for this course!</p>
                                    <p className="font-12 regular-text grey-text mb-1">Please contact admin for any further help</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    // -- Render the correct modal --
    if (modalMode === 'register') return renderRegisterModal();
    if (modalMode === 'edit') return renderEditModal();
    if (modalMode === 'reschedule') return renderRescheduleModal();
    if (modalMode === 'reschedule-empty') return renderRescheduleEmptyState();
    if (modalMode === 'cancel-confirm') {
        return (
            <ConfirmationPopup
                open={true}
                message="Are you sure you want to cancel this registration?"
                subMessage="If you cancel your registration and no other slots are availabale, you will be unable to attend this course."
                onClose={() => setModalMode('edit')}
                onConfirm={handleConfirmCancel}
                loading={loading}
            />
        );
    }

    return null;
};
